import "phoenix_html"
import { Socket } from "phoenix"
import Cookies from "js-cookie"
import Hls from "hls.js"

import { matchMqBreakpoint } from "../utils/mq"
import Uploaders from "../utils/upload"
import ReconnectingLiveSocket from "../utils/reconnecting-live-socket"

export default function initLiveSocket({ hooks = {} }) {
  const liveSocket = new ReconnectingLiveSocket(`/live`, Socket, {
    heartbeatIntervalMs: 15_000,
    params: {
      _csrf_token: document
        .querySelector(`meta[name='csrf-token']`)
        .getAttribute(`content`),
    },
    hooks,
    uploaders: Uploaders,
  })

  // liveSocket.enableDebug()
  liveSocket.disableDebug()
  // liveSocket.enableLatencySim(2000)
  liveSocket.disableLatencySim()
  liveSocket.connect()

  window.liveSocket = liveSocket

  // HELPER FUNCTIONS:

  window.liveSocketPutFlash = (type, text) => {
    const el = document.querySelector(`[data-phx-main]`)
    if (el)
      window.liveSocket?.execJS(
        el,
        JSON.stringify([[`push`, { event: `put_flash`, data: { type, text } }]])
      )
  }

  // EVENT HANDLERS:

  window.addEventListener(`phx:exec-js`, ({ detail }) => {
    // executes phx js object, string or attribute value containing stringified phx js object

    if (detail.mq && !matchMqBreakpoint(detail.mq)) return

    if (detail.unless_flash) {
      let skipBecauseOfFlash = false
      const flashNodes = document.querySelectorAll(`.flash`)

      if (flashNodes.length) {
        ;[...flashNodes].some((node) => {
          if (!node.hidden && !node.classList.contains(`hidden`)) {
            skipBecauseOfFlash = true
            return true
          }
          return false
        })
      }

      if (skipBecauseOfFlash) return
    }

    const exec = () => {
      ;(detail.to
        ? document.querySelectorAll(detail.to)
        : [detail.dispatcher]
      ).forEach((el) => {
        if (el) {
          let js = ``
          if (detail.attr) js = el.getAttribute(detail.attr)
          else if (detail.js) {
            if (detail.js.ops) js = JSON.stringify(detail.js.ops)
            else js = detail.js
          }

          if (js) liveSocket?.execJS(el, js)
        }
      })
    }

    if (detail.timeout) window.setTimeout(exec, detail.timeout)
    else exec()
  })

  window.addEventListener(`phx:dispatch-event`, ({ detail }) => {
    document
      .getElementById(detail.to)
      ?.dispatchEvent(new Event(detail.type, { bubbles: true }))
  })

  window.addEventListener(`phx:click`, ({ detail }) => {
    document.querySelectorAll(detail.to).forEach((el) => el.click())
  })

  window.addEventListener(`phx:submit`, (e) => {
    document.getElementById(e.detail.id)?.submit()
  })

  window.addEventListener(`phx:reset-form`, ({ detail }) => {
    document.getElementById(detail.id)?.reset()
  })

  window.addEventListener(`phx:close-details`, ({ detail }) => {
    const details = document.getElementById(detail.id)
    if (details) details.open = false
  })

  window.addEventListener(`phx:class-add`, ({ detail }) => {
    window.setTimeout(() => {
      const { addClass, removeClass } = liveSocket.js()
      const selectors = Array.isArray(detail.to) ? detail.to : [detail.to]

      selectors.forEach((selector) => {
        document
          .querySelectorAll(selector)
          .forEach((el) => addClass(el, detail.class))
      })

      if (detail.duration) {
        window.setTimeout(() => {
          selectors.forEach((selector) => {
            document
              .querySelectorAll(selector)
              .forEach((el) => removeClass(el, detail.class))
          })
        }, detail.duration)
      }
    }, 100)
  })

  window.addEventListener(`phx:class-add-closest`, (e) => {
    const closest = e.target.closest(e.detail.closest)
    if (closest) liveSocket.js().addClass(closest, e.detail.class)
  })

  window.addEventListener(`phx:class-remove-closest`, (e) => {
    const closest = e.target.closest(e.detail.closest)
    if (closest) liveSocket.js().removeClass(closest, e.detail.class)
  })

  window.addEventListener(`phx:scroll-into-view`, ({ detail }) => {
    if (detail.mq && !matchMqBreakpoint(detail.mq)) return

    window.setTimeout(() => {
      document
        .querySelector(detail.to)
        ?.scrollIntoView({ block: detail.block || `center` })
    }, 100)
  })

  window.addEventListener(`phx:scroll-to-top`, ({ detail }) => {
    if (detail.mq && !matchMqBreakpoint(detail.mq)) return

    window.scrollTo({ top: 0, left: 0, behavior: `instant` })
  })

  window.addEventListener(`phx:download`, ({ detail }) => {
    const { data, filename, content_type } = detail
    const bytes = Uint8Array.from(atob(data), (c) => c.charCodeAt(0))
    const blob = new Blob([bytes], { type: content_type })
    const url = URL.createObjectURL(blob)
    const a = document.createElement(`a`)
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  })

  window.addEventListener(`phx:set-cookie`, ({ detail }) => {
    const options = {}
    if (detail.expires) options.expires = detail.expires
    Cookies.set(detail.name, detail.value, options)
  })

  window.addEventListener(`phx:fix-hls-video`, ({ detail }) => {
    ;(detail.to
      ? document.querySelectorAll(detail.to)
      : [detail.dispatcher]
    ).forEach((el) => {
      if (
        el.canPlayType(`application/vnd.apple.mpegurl`) != `probably` &&
        Hls.isSupported()
      ) {
        const hls = new Hls()
        hls.loadSource(el.src)
        hls.attachMedia(el)
      }
    })
  })
}
